import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Filter, SavedView, Sort, WriteResult } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

/** What a screen hands over when it saves what it is currently looking at. */
export interface ViewDraft {
  name: string;
  filter: Filter;
  columns: string[];
  sort: Sort[];
}

/**
 * Saved views for one scope, backed by the data-access interface.
 *
 * This was an in-memory stub while nothing implemented `SavedViewsRepository`.
 * It now calls the interface like every other screen, so swapping the fixture
 * adapter for the real API changes nothing here.
 *
 * `activeViewId` starts on the scope's default — opening a section applies
 * its default view — and resets when the scope changes. The reset is derived
 * during render rather than written by an effect: an effect that calls
 * setState causes a cascading render, and React's own guidance is to adjust
 * state from props directly when the value can simply be computed.
 */
export function useSavedViews(scope: string): {
  views: SavedView[];
  activeViewId: string | null;
  defaultView: SavedView | null;
  select: (id: string | null) => void;
  save: (draft: ViewDraft) => Promise<WriteResult<SavedView>>;
  rename: (id: string, name: string) => Promise<WriteResult<SavedView>>;
  /** Overwrite a view's definition with what the list is showing now. */
  redefine: (
    id: string,
    draft: Omit<ViewDraft, "name">,
  ) => Promise<WriteResult<SavedView>>;
  setDefault: (id: string) => Promise<WriteResult<SavedView>>;
  remove: (id: string) => Promise<WriteResult<void>>;
  isPending: boolean;
} {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["saved-views", scope],
    queryFn: () => data.savedViews.list(scope),
  });

  const views = query.data ?? [];
  const defaultView = views.find((view) => view.isDefault) ?? null;

  const [selection, setSelection] = useState<{
    scope: string;
    id: string | null;
  } | null>(null);

  const activeViewId =
    selection?.scope === scope ? selection.id : (defaultView?.id ?? null);

  /**
   * Every mutation invalidates rather than patching the cache.
   *
   * `setDefault` un-defaults every sibling in the same scope, so a local
   * patch would have to replicate that rule in a second place — and the
   * moment the two disagree, two views claim to be the default.
   */
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["saved-views", scope] });

  const saveMutation = useMutation({
    mutationFn: (draft: ViewDraft) =>
      data.savedViews.create({
        scope,
        name: draft.name,
        // Shared, not per-user (§2.1). There are no private views in v1.
        isShared: true,
        isDefault: false,
        filter: draft.filter,
        columns: draft.columns,
        sort: draft.sort,
      }),
    onSuccess: invalidate,
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      data.savedViews.update(id, { name }),
    onSuccess: invalidate,
  });

  /**
   * Separate from `rename` because they are different acts: renaming changes
   * what a view is called, this changes what it means. Sharing one mutation
   * would let a rename that raced a redefine write back a stale filter.
   */
  const redefineMutation = useMutation({
    mutationFn: ({
      id,
      draft,
    }: {
      id: string;
      draft: Omit<ViewDraft, "name">;
    }) =>
      data.savedViews.update(id, {
        filter: draft.filter,
        columns: draft.columns,
        sort: draft.sort,
      }),
    onSuccess: invalidate,
  });

  const defaultMutation = useMutation({
    mutationFn: (id: string) => data.savedViews.setDefault(id),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => data.savedViews.delete(id),
    onSuccess: invalidate,
  });

  return {
    views,
    activeViewId,
    defaultView,
    select: (id: string | null) => {
      setSelection({ scope, id });
    },
    save: (draft) => saveMutation.mutateAsync(draft),
    rename: (id, name) => renameMutation.mutateAsync({ id, name }),
    redefine: (id, draft) => redefineMutation.mutateAsync({ id, draft }),
    setDefault: (id) => defaultMutation.mutateAsync(id),
    remove: (id) => deleteMutation.mutateAsync(id),
    isPending:
      saveMutation.isPending ||
      renameMutation.isPending ||
      redefineMutation.isPending ||
      defaultMutation.isPending ||
      deleteMutation.isPending,
  };
}
